import type { Invoice } from '@fpc/shared';
import { Money } from '@/components/ui';

/**
 * GST under reverse charge (RCM): one tick on the invoice. Ticked, the GST on
 * the invoice is the company's to pay to the government, not the vendor's to
 * collect. Set at review and confirmed at accounting, for reporting only — it
 * is never part of what the vendor is paid.
 */
export function RcmCheckbox({
  checked,
  onChange,
  gst,
  currency,
  showAmount = true,
}: {
  checked: boolean;
  onChange(checked: boolean): void;
  /** The GST on the invoice: under RCM, all of it is reversed. */
  gst: number;
  currency: string;
  /** Off where the amount is already shown beside the figures. */
  showAmount?: boolean;
}) {
  return (
    <div className="space-y-2 sm:col-span-2">
      <label
        className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors ${
          checked ? 'border-amber-300 bg-amber-50/70' : 'border-slate-200 hover:bg-slate-50'
        }`}
      >
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 accent-brand-600"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span className="min-w-0">
          <span className="block text-sm font-medium text-slate-800">RCM (reverse charge)</span>
          <span className="block text-xs text-slate-500">
            The GST on this invoice is paid by the company, not the vendor.
          </span>
        </span>
      </label>
      {checked && showAmount ? (
        gst > 0 ? (
          <p className="text-sm">
            GST reversed under RCM:{' '}
            <Money currency={currency} minor={gst} className="font-semibold text-amber-700" />
            <span className="block text-xs text-slate-500">
              Payable by the company, for reporting only: the vendor is paid the same.
            </span>
          </p>
        ) : (
          <p className="notice-warning text-xs">This invoice has no GST to reverse.</p>
        )
      ) : null}
    </div>
  );
}

/** RCM as recorded, read-only. */
export function RcmSummary({ invoice }: { invoice: Invoice }) {
  if (!invoice.rcmApplicable) {
    return <p className="text-sm text-slate-500">Not applicable</p>;
  }
  return (
    <p className="text-sm text-amber-700">
      <Money currency={invoice.currency} minor={invoice.rcmAmount} />
      <span className="text-xs text-slate-500">
        {' '}
        GST reversed — payable by the company, not part of the vendor payment
      </span>
    </p>
  );
}
